// Colunas da visao "planilha GRUPO GPF" (B6:AF23) derivadas do que o motor ja entrega
// (plannedX, metaCrc, recepcao, dezenaBreakdown). Nao recalcula funil: so faixas de bonus,
// trilha Comercial e dezenas em R$, que o motor nao devolve. Nao editar o motor por aqui.
import { roundUp } from './funnelEngine';

export const BIG_MULTIPLIER = 1.25;

const n = (v) => Number(v) || 0;

// Dias uteis por dezena: preferencia pelo breakdown salvo; fallback para o relatorio.
export function getDezenaDays(unit, report) {
  const fromBreakdown = (unit?.dezenaBreakdown || []).slice(0, 3).map(d => n(d.businessDays));
  if (fromBreakdown.length === 3) return fromBreakdown;
  return [n(report?.dezena1BusinessDays), n(report?.dezena2BusinessDays), n(report?.dezena3BusinessDays)];
}

// Faixas de bonus (M/N/O e X/Y/Z): ate 80% | 80-99,99% | 100%+ sobre os comparecimentos
export function computeFaixas(comparecimentos) {
  const top = n(comparecimentos);
  const low = Math.floor(top * 0.8 + 1e-9);
  return { low, mid: Math.max(top - 1, low), high: top };
}

// Faixa de bonus atingida pelo realizado (maior limite alcancado); null = abaixo da primeira
export function faixaAtingida(realizado, faixas) {
  const r = n(realizado);
  if (r >= faixas.high && faixas.high > 0) return 'high';
  if (r >= faixas.mid && faixas.mid > 0) return 'mid';
  if (r >= faixas.low && faixas.low > 0) return 'low';
  return null;
}

export function buildSheetRow(unit, report) {
  const days = getDezenaDays(unit, report);
  const totalDays = days.reduce((a, b) => a + b, 0);
  const legacy = unit.legacyShare === true || (unit.agencyShareEffective === undefined && report?.agencyShare == null && unit.agencyShare == null);
  const meta = n(unit.metaReceita);
  const metaCrc = unit.metaCrc !== undefined && unit.metaCrc !== null ? n(unit.metaCrc) : meta;
  const metaRecepcao = legacy ? 0 : n(unit.metaRecepcao);
  const comp = n(unit.plannedComparecimentos);
  const investimento = n(unit.plannedInvestimento);
  const rec = !legacy && unit.recepcao ? unit.recepcao : null;
  const ticketRecepcao = rec ? n(rec.ticketRecepcao) : 0;
  const breakdown = unit.dezenaBreakdown || [];

  const crcFaixas = computeFaixas(comp);
  const recFaixas = rec ? computeFaixas(rec.comparecimentos) : null;
  const perDezena = (i) => (totalDays > 0 ? (meta / totalDays) * days[i] : 0);

  return {
    id: unit.id || unit.routineId || unit.unitMappingId,
    name: unit.routineName,
    legacy,
    days,
    totalDays,
    meta, metaCrc, metaRecepcao,
    investimento,
    pctInvest: meta > 0 ? investimento / meta : 0,
    ticketUnidade: n(unit.ticketMedio),
    fechamentos: n(unit.plannedFechamentos),
    comparecimentos: comp,
    agendamentos: n(unit.plannedAgendamentos),
    leads: n(unit.plannedLeads),
    cpl: n(unit.cplMedio),
    crcFaixas,
    dezenasCrc: [0, 1, 2].map(i => n(breakdown[i]?.plannedComparecimentos)),
    recepcao: rec && {
      meta: metaRecepcao,
      fechamentos: n(rec.fechamentos),
      comparecimentos: n(rec.comparecimentos),
      agendamentos: n(rec.agendamentos),
      indicacoes: n(rec.leads),
      faixas: recFaixas
    },
    comercial: rec && ticketRecepcao > 0 ? {
      fechamentos: roundUp(meta / ticketRecepcao),
      valorTicket: meta,
      dezenasValor: [0, 1, 2].map(perDezena),
      ticketUnidade: ticketRecepcao
    } : null,
    real: {
      receita: n(unit.realizadoReceita),
      investimento: n(unit.realizadoInvestimento),
      leads: n(unit.realizadoLeads),
      agendamentos: n(unit.realizadoAgendamentos),
      comparecimentos: n(unit.realizadoComparecimentos),
      fechamentos: n(unit.realizadoFechamentos),
      dezenasComp: [0, 1, 2].map(i => n(breakdown[i]?.realizadoComparecimentos))
    },
    // Card da unidade (B25:F34)
    card: {
      dezenas: [0, 1, 2].map(i => {
        const metaD = n(breakdown[i]?.plannedComparecimentos);
        return { dias: days[i], meta: metaD, big: metaD * BIG_MULTIPLIER, valor: perDezena(i) };
      })
    }
  };
}

export function buildSheetTotals(rows) {
  const sum = (fn) => rows.reduce((a, r) => a + fn(r), 0);
  const rec = rows.filter(r => r.recepcao);
  const com = rows.filter(r => r.comercial);
  const t = {
    meta: sum(r => r.meta),
    metaCrc: sum(r => r.metaCrc),
    investimento: sum(r => r.investimento),
    fechamentos: sum(r => r.fechamentos),
    comparecimentos: sum(r => r.comparecimentos),
    agendamentos: sum(r => r.agendamentos),
    leads: sum(r => r.leads),
    crcFaixas: {
      low: sum(r => r.crcFaixas.low), mid: sum(r => r.crcFaixas.mid), high: sum(r => r.crcFaixas.high)
    },
    dezenasCrc: [0, 1, 2].map(i => sum(r => r.dezenasCrc[i])),
    recepcao: rec.length ? {
      meta: rec.reduce((a, r) => a + r.recepcao.meta, 0),
      fechamentos: rec.reduce((a, r) => a + r.recepcao.fechamentos, 0),
      comparecimentos: rec.reduce((a, r) => a + r.recepcao.comparecimentos, 0),
      agendamentos: rec.reduce((a, r) => a + r.recepcao.agendamentos, 0),
      indicacoes: rec.reduce((a, r) => a + r.recepcao.indicacoes, 0),
      faixas: {
        low: rec.reduce((a, r) => a + r.recepcao.faixas.low, 0),
        mid: rec.reduce((a, r) => a + r.recepcao.faixas.mid, 0),
        high: rec.reduce((a, r) => a + r.recepcao.faixas.high, 0)
      }
    } : null,
    comercial: com.length ? {
      fechamentos: com.reduce((a, r) => a + r.comercial.fechamentos, 0),
      valorTicket: com.reduce((a, r) => a + r.comercial.valorTicket, 0),
      dezenasValor: [0, 1, 2].map(i => com.reduce((a, r) => a + r.comercial.dezenasValor[i], 0))
    } : null,
    real: {
      receita: sum(r => r.real.receita),
      investimento: sum(r => r.real.investimento),
      leads: sum(r => r.real.leads),
      agendamentos: sum(r => r.real.agendamentos),
      comparecimentos: sum(r => r.real.comparecimentos),
      fechamentos: sum(r => r.real.fechamentos),
      dezenasComp: [0, 1, 2].map(i => sum(r => r.real.dezenasComp[i]))
    }
  };
  t.pctInvest = t.meta > 0 ? t.investimento / t.meta : 0;
  // ponderados, como G23 (=D/H) e L23 (=E/K) da planilha
  t.ticketUnidade = t.fechamentos > 0 ? t.metaCrc / t.fechamentos : 0;
  t.cpl = t.leads > 0 ? t.investimento / t.leads : 0;
  if (t.recepcao && t.recepcao.fechamentos > 0) t.recepcao.ticket = t.recepcao.meta / t.recepcao.fechamentos;
  if (t.comercial && t.comercial.fechamentos > 0) t.comercial.ticketUnidade = t.comercial.valorTicket / t.comercial.fechamentos;
  return t;
}
